import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Clock,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Plus,
  Calendar
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const AdminTimesheetsPage = () => {
  const [timesheets, setTimesheets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTimesheet, setNewTimesheet] = useState({ project_id: '', task_description: '', hours_worked: 8, work_date: new Date().toISOString().split('T')[0] });
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [tsRes, projRes] = await Promise.all([
        axios.get(`${API}/admin/timesheets`, { headers }),
        axios.get(`${API}/admin/hr-projects`, { headers })
      ]);
      setTimesheets(tsRes.data.timesheets);
      setProjects(projRes.data.projects);
    } catch (error) {
      if (error.response?.status === 401) navigate("/admin-login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token, navigate]);

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/admin/timesheets`, newTimesheet, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Timesheet submitted");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to submit timesheet");
    }
  };

  const handleApproval = async (id, action) => {
    try {
      await axios.put(`${API}/admin/timesheets/${id}/approve`, { action }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Timesheet ${action}d`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} timesheet`);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center"><div className="text-[#c9a227]">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#0b1c2d] lg:ml-64 p-6" data-testid="admin-timesheets-page">
      <div className="mb-6">
        <Link to="/admin" className="flex items-center gap-2 text-slate-400 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" />Back to Dashboard</Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Timesheets</h1>
            <p className="text-slate-400 mt-1">{timesheets.length} entries</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary"><Plus className="w-4 h-4 mr-2" />Submit Timesheet</Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700">
              <DialogHeader><DialogTitle className="text-white">Submit Timesheet</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Project</Label>
                  <select value={newTimesheet.project_id} onChange={(e) => setNewTimesheet({...newTimesheet, project_id: e.target.value})} className="w-full mt-1 p-2 bg-slate-800 border border-slate-700 rounded-sm text-white">
                    <option value="">Select project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-slate-300">Work Date</Label>
                  <Input type="date" value={newTimesheet.work_date} onChange={(e) => setNewTimesheet({...newTimesheet, work_date: e.target.value})} className="mt-1 bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Hours Worked</Label>
                  <Input type="number" min="0.5" max="16" step="0.5" value={newTimesheet.hours_worked} onChange={(e) => setNewTimesheet({...newTimesheet, hours_worked: parseFloat(e.target.value)})} className="mt-1 bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Task Description</Label>
                  <Textarea value={newTimesheet.task_description} onChange={(e) => setNewTimesheet({...newTimesheet, task_description: e.target.value})} className="mt-1 bg-slate-800 border-slate-700 text-white" placeholder="Describe work completed..." />
                </div>
                <Button onClick={handleSubmit} className="w-full btn-primary">Submit</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr><th>Employee</th><th>Date</th><th>Hours</th><th>Task</th><th>Rate</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {timesheets.slice(0, 30).map((ts) => (
                  <tr key={ts.id}>
                    <td className="text-white">{ts.user_name}</td>
                    <td className="text-slate-400">{ts.work_date}</td>
                    <td className="text-[#c9a227] font-semibold">{ts.hours_worked}h</td>
                    <td className="text-slate-400 max-w-[200px] truncate">{ts.task_description}</td>
                    <td className="text-slate-400">${ts.hourly_rate}/hr</td>
                    <td>
                      <Badge className={ts.status === 'approved' ? 'status-green' : ts.status === 'rejected' ? 'status-red' : 'status-yellow'}>{ts.status}</Badge>
                    </td>
                    <td>
                      {ts.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleApproval(ts.id, 'approve')} className="bg-green-600 hover:bg-green-700 h-7 px-2"><CheckCircle className="w-4 h-4" /></Button>
                          <Button size="sm" onClick={() => handleApproval(ts.id, 'reject')} className="bg-red-600 hover:bg-red-700 h-7 px-2"><XCircle className="w-4 h-4" /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTimesheetsPage;
